import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router";
import { useNavigate } from "react-router";
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  SxProps,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { SidebarWidth } from "@/theme/Theme-variable";
import { isMobileDevice } from "@/utils/browser";
import {
  hideSideNav,
  openProjectTeammateInvite,
} from "@/store/sideNav/sideNavActions";
import { doLogout } from "@/store/logout/logoutActions";
import LanguagePicker from "@/components/Common/LanguagePicker/LanguagePicker";
import { useTranslation } from "react-i18next";
import CustomModal from "@/components/Common/CustomModal/CustomModal";
import InviteTeammate from "@/Pages/Project/components/InviteTeammate";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import usePrevious from "@/hooks/usePrevious";
import Tooltip from "@mui/material/Tooltip";
import { setMyDefaultProject } from "@/store/auth/authActions";

import BrandHeader from "./BrandHeader/BrandHeader";
import ProjectMenu from "./ProjectMenu/ProjectMenu";
import MainMenu from "./MainMenu/MainMenu";
import HubMenu from "./HubMenu/HubMenu";
import MyEventsMenu from "./MyEvents/MyEventsMenu";
import DashboardMenu from "./DashboardMenu/DashboardMenu";
import CreateProjectMenu from "./CreateProjectMenu/CreateProjectMenu";

interface Teammate {
  first_name?: string;
  last_name?: string;
  user_id?: string;
}

interface AuthProject {
  id: string | number;
  name: string;
  role_type: string;
  level: string | number;
  teammates: Teammate[];
}

interface AuthHub {
  id: string | number;
  name: string;
  role_level: string;
}

interface NavProject {
  id: string | number;
  level: string | number;
  projectName: string;
  roleType: string;
  members: Array<{ firstName: string; lastName: string; userId: string }>;
  hasPermission: boolean;
  isDefault: boolean;
  selected: boolean;
}

interface NavHub {
  id: string | number;
  hubName: string;
  roleLevel: string;
  selected: boolean;
}

interface NavState {
  subscriptionModal: boolean;
  openChangeDefaultProjectModal: boolean;
  defaultProjectOpened: boolean;
  inviteProjectTeammateModal: boolean;
  inviteTeammateProjectId: string | number | null;
}

const mobileMenuLinkSx: SxProps<Theme> = {
  color: "common.white",
  fontSize: "0.8em",
  letterSpacing: "0.7px",
  justifyContent: "flex-start",
  px: "8px",
  py: "8px",
  width: "100%",
  borderRadius: 0,
  textTransform: "none",
};

const Sidebar = () => {
  const { t } = useTranslation(["translation.navigation"]);

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const [moreOpen, setMoreOpen] = useState(false);

  // TODO clean up this code
  const [navState, setNavState] = useState<NavState>({
    subscriptionModal: false,
    openChangeDefaultProjectModal: false,
    defaultProjectOpened: false,
    inviteProjectTeammateModal: false,
    inviteTeammateProjectId: null,
  });

  const { sideNav, user } = useSelector((state: any) => state);

  const authUser = useSelector((state: any) => state.authUser);

  const defaultProjectId: string | number | null =
    authUser?.default_project_id || null;

  const location = useLocation();

  const [userProjects, setUserProjects] = useState<NavProject[]>([]);

  const [clientHubs, setClientHubs] = useState<NavHub[]>([]);

  const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));

  const isSmallScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );

  const isLoggedIn = !!user.get("jwt");

  const isSideNavOpen: boolean = sideNav.get("menuOpen");

  const inviteTeammateModalForProject: string | number | null = sideNav.get(
    "projectInviteTeammate"
  );
  const prevInviteTeammateModalForProject = usePrevious(
    inviteTeammateModalForProject
  );

  const close = () => {
    dispatch(hideSideNav());
  };

  const onCreateNewProject = () => {
    navigate(`/project/create-project`);
  };

  const handleMenuItem = () => {
    if (isMobileDevice()) {
      close();
    }
  };

  useEffect(() => {
    const projectList: NavProject[] = authUser?.projects?.map(
      (p: AuthProject) => {
        const { id, name, role_type, level, ...rest } = p;
        const { pathname } = location;
        const isOwner = role_type?.toLowerCase() === "owner";
        let members: NavProject["members"] = [];
        if (isOwner) {
          members.push({
            firstName: user?.get("first_name"),
            lastName: user?.get("last_name"),
            userId: user?.get("id"),
          });
        }
        members = [
          ...members,
          ...rest.teammates.map((tm) => ({
            firstName: tm?.first_name || "",
            lastName: tm?.last_name || "",
            userId: tm?.user_id || "",
          })),
        ];
        return {
          id,
          level,
          projectName: name,
          roleType: role_type,
          members,
          hasPermission: role_type !== "Support Member",
          isDefault: defaultProjectId === id,
          selected:
            defaultProjectId === id || pathname.startsWith(`/project/${id}`),
        };
      }
    );
    setUserProjects(
      projectList.sort((first, second) => {
        return first.isDefault ? -1 : second.isDefault ? 1 : 0;
      })
    );
  }, [
    authUser?.projects,
    authUser?.default_project_id,
    location?.pathname,
    defaultProjectId,
    location,
    user,
  ]);

  useEffect(() => {
    setClientHubs(
      authUser?.hubs?.map((p: AuthHub) => {
        const { id, name, role_level } = p;
        const { pathname } = location;
        return {
          id,
          hubName: name,
          roleLevel: role_level,
          selected: pathname.startsWith(`/hubs/${id}`),
        };
      })
    );
  }, [authUser?.hubs, location?.pathname, location]);

  useEffect(() => {
    if (
      inviteTeammateModalForProject &&
      inviteTeammateModalForProject !== prevInviteTeammateModalForProject
    ) {
      setNavState((prevNavState) => ({
        ...prevNavState,
        inviteProjectTeammateModal: true,
        inviteTeammateProjectId: inviteTeammateModalForProject,
      }));
    }
  }, [
    inviteTeammateModalForProject,
    navState,
    prevInviteTeammateModalForProject,
  ]);

  const logout = () => {
    dispatch(doLogout());
  };

  const changeDefaultProject = (project_id: string | number) => {
    dispatch(setMyDefaultProject(project_id));
  };

  const clearProjectInviteTeammate = () => {
    dispatch(openProjectTeammateInvite(null));
  };

  const handleCustomModalClose = () => {
    setNavState({ ...navState, subscriptionModal: false });
  };

  const handleFirstChoice = () => {
    navigate("/contact-us");
  };

  const handleClickChangeDefaultProject = (e: React.MouseEvent) => {
    e.stopPropagation();
    setNavState({ ...navState, openChangeDefaultProjectModal: true });
  };

  const handleCloseChangeDefaultProject = () => {
    setNavState({ ...navState, openChangeDefaultProjectModal: false });
  };

  const handleChangeDefaultProject = (project_id: string | number) => {
    changeDefaultProject(project_id);
    setNavState({ ...navState, openChangeDefaultProjectModal: false });
  };

  const handleInviteProjectTeammate = (
    state = false,
    pId: string | number | null = null
  ) => {
    if (!state && inviteTeammateModalForProject) {
      clearProjectInviteTeammate();
    }
    setNavState({
      ...navState,
      inviteProjectTeammateModal: state,
      inviteTeammateProjectId: pId,
    });
  };

  const addTeamMember = (
    e: React.MouseEvent,
    projectId: string | number
  ) => {
    e.stopPropagation();
    handleInviteProjectTeammate(true, projectId);
  };

  const renderMobileMenu = () => (
    <Box sx={{ display: { xs: "block", md: "none" } }}>
      <Button
        fullWidth
        onClick={() => setMoreOpen((prev) => !prev)}
        endIcon={moreOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{
          color: "common.white",
          fontSize: "0.9em",
          mb: "0.5em",
          py: "5px",
          textTransform: "none",
          justifyContent: "center",
        }}
      >
        {t("More")}
      </Button>
      <Collapse in={moreOpen}>
        <Button
          component={Link}
          to="/settings"
          onClick={handleMenuItem}
          sx={mobileMenuLinkSx}
        >
          {t("settings")}
        </Button>
        <Button
          component="a"
          href="https://rebelbase.zendesk.com/hc/en-us"
          onClick={handleMenuItem}
          sx={mobileMenuLinkSx}
        >
          {t("help")}
        </Button>
        <Box sx={{ px: "8px" }}>
          <LanguagePicker />
        </Box>
        <Button onClick={logout} sx={mobileMenuLinkSx}>
          {t("logOut")}
        </Button>
      </Collapse>
    </Box>
  );

  const renderChangeDefaultProjectModal = () => {
    const iDefaultProject = userProjects.find((p) => p.isDefault);

    return (
      <Dialog
        open={navState.openChangeDefaultProjectModal}
        onClose={handleCloseChangeDefaultProject}
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            textTransform: "uppercase",
            color: "primary.main",
            pt: "20px",
          }}
        >
          {t("changeDefaultProject")}
          <IconButton
            onClick={handleCloseChangeDefaultProject}
            size="small"
            sx={{ ml: 2 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ maxWidth: 400, mb: "2rem", color: "grey.600" }}>
            {t("defaultProjectNote")}
          </Typography>
          {iDefaultProject && (
            <Typography sx={{ maxWidth: 400 }}>
              <Box component="span" sx={{ textTransform: "uppercase" }}>
                {t("currentDefault")}
              </Box>{" "}
              {iDefaultProject.projectName}
            </Typography>
          )}
          <List
            sx={{
              mt: "20px",
              p: 0,
              "&:hover .MuiListItemButton-root:not(:hover)": {
                opacity: 0.5,
                backgroundColor: "common.white",
                color: "secondary.main",
              },
            }}
          >
            {userProjects.map((p) => (
              <ListItemButton
                key={`project-${p.id}`}
                onClick={() => handleChangeDefaultProject(p.id)}
                selected={p.id === defaultProjectId}
                sx={{
                  py: "10px",
                  pl: "20px",
                  width: { xs: "100%", md: 400 },
                  "&.Mui-selected": {
                    backgroundColor: "primary.light",
                    color: "common.white",
                    "&:hover": {
                      backgroundColor: "primary.light",
                      color: "common.white",
                    },
                  },
                  "&:hover": {
                    backgroundColor: "primary.light",
                    color: "common.white",
                  },
                }}
              >
                <ListItemText primary={p.projectName} />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    );
  };

  const MenuContent = (
    <>
      <Card
        id="sideNav"
        key="sideNav"
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          backgroundColor: (theme) => theme.palette.secondary.main,
          color: (theme) => theme.palette.background.default,
          borderRadius: 0,
        }}
      >
        <CardContent
          sx={{
            "&:last-child": {
              padding: 0,
            },
            "&::-webkit-scrollbar": {
              width: "10px",
              "&-track": {
                background: "transparent",
              },
              "&-thumb": {
                background: (theme) => theme.palette.secondary.main,
                "&:hover": {
                  background: (theme) => theme.palette.secondary.main,
                },
              },
            },
            padding: 0,
            overflowY: "auto",
            overflowX: "hidden",
            flex: 1,
          }}
        >
          <BrandHeader isSmallScreen={isSmallScreen} collapse={close} />

          <DashboardMenu />

          <MainMenu
            onItemClick={onCreateNewProject}
            label={<>{t("Project")}</>}
            endIcon={
              <Tooltip title={t("addProject")}>
                <AddIcon />
              </Tooltip>
            }
          />
          {userProjects?.length > 0 ? (
            userProjects?.map((m) => (
              <ProjectMenu
                key={m?.id}
                {...m}
                addTeamMember={addTeamMember}
                setMyDefaultProject={handleClickChangeDefaultProject}
                onMenuClick={handleMenuItem}
              />
            ))
          ) : (
            <CreateProjectMenu />
          )}

          {clientHubs?.length > 0 && (
            <>
              <MainMenu label={<>{t("Hub")}</>} />
              {clientHubs?.map((hub) => (
                <HubMenu key={hub?.id} {...hub} onMenuClick={handleMenuItem} />
              ))}
            </>
          )}

          <MainMenu label={<>{t("MyEvents")}</>} />
          <MyEventsMenu />

          <Box sx={{ mt: "10px" }}>{renderMobileMenu()}</Box>
          {renderChangeDefaultProjectModal()}
        </CardContent>
      </Card>
    </>
  );

  const SidebarContent = (
    <>
      {MenuContent}
      {/*TODO migrate below modal popup to material*/}
      <CustomModal
        header={t("SubscriptionExpired")}
        message={t("SubscriptionExpiredMessage")}
        firstChoice={t("FirstChoice")}
        cancel={t("Cancel")}
        isOpen={navState.subscriptionModal}
        handleCancel={handleCustomModalClose}
        handleFirstChoice={handleFirstChoice}
      />
      <InviteTeammate
        isOpen={navState.inviteProjectTeammateModal}
        projectId={navState.inviteTeammateProjectId}
        close={handleInviteProjectTeammate}
      />
    </>
  );

  if (!isLoggedIn) {
    return null;
  }

  if (lgUp) {
    return (
      <Drawer
        anchor="left"
        open={isSideNavOpen}
        variant="persistent"
        sx={{
          "& .MuiDrawer-paper": {
            zIndex: 11,
          },
        }}
        slotProps={{
          paper: {
            sx: {
              width: SidebarWidth,
              borderRight: 0,
            },
          }
        }}
      >
        {SidebarContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      anchor="right"
      open={isSideNavOpen}
      onClose={close}
      sx={{
        zIndex: 900,
        "& .MuiDrawer-paper": {
          zIndex: 11,
        },
      }}
      variant="temporary"
      slotProps={{
        paper: {
          sx: {
            width: SidebarWidth,
            borderRight: 0,
          },
        }
      }}
    >
      {SidebarContent}
    </Drawer>
  );
};

export default React.memo(Sidebar);
